import React from 'react'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import { NameDto } from '../../../api/Common/Types'
import { AddUpdateInstrument } from '../../../api/Trading-Accounts/Types/AddUpdateInstrument'
import { WalletCurrencyDto } from '../../../api/Wallet/Types/WalletCurrencyDto'
import { isNumeric } from '../../../utils/is-numeric'

export type AddEditInstrumentFormValues = {
  id?: string
  name: string
  typeId: number
  symbol: string
  baseCurrencyId: string
  quoteCurrencyId: string
  withholdingTax: number
  contractSize: number
  digits: number
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  instrument?: InstrumentDto<string>
  instrumentTypes: NameDto[]
  currencies: WalletCurrencyDto[]

  onSubmit(values: FormSubmitValues<AddEditInstrumentFormValues>): Promise<void>
}

const AddEditInstrumentFormUI: React.FC<FormikProps<AddEditInstrumentFormValues> & OuterProps> = (
  props
) => {
  const {
    instrument,
    instrumentTypes,
    currencies,
    handleSubmit,
    isSubmitting,
    isValid,
    touched,
    errors,
  } = props

  const handleSubmitForm = () => {
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{instrument ? 'Edit Instrument' : 'Add Instrument'}</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormField name='name' label='Name' error={touched.name ? errors.name : undefined} />
            <FormField
              name='symbol'
              label='Symbol'
              error={touched.symbol ? errors.symbol : undefined}
            />
            <FormSelectField
              name='typeId'
              label='Type'
              options={(instrumentTypes || []).map((type) => ({
                id: type.id,
                name: type.name ?? '',
              }))}
              error={touched.typeId ? errors.typeId : undefined}
              onChange={(value) => props.setFieldValue('typeId', Number(value))}
            />
            <FormSelectField
              name='baseCurrencyId'
              label='Base Currency'
              options={(currencies || []).map((currency) => ({
                id: currency.id,
                name: currency.id,
              }))}
              error={touched.baseCurrencyId ? errors.baseCurrencyId : undefined}
              onChange={(value) => props.setFieldValue('baseCurrencyId', String(value))}
            />
            <FormSelectField
              name='quoteCurrencyId'
              label='Quote Currency'
              options={(currencies || []).map((currency) => ({
                id: currency.id,
                name: currency.id,
              }))}
              error={touched.quoteCurrencyId ? errors.quoteCurrencyId : undefined}
              onChange={(value) => props.setFieldValue('quoteCurrencyId', String(value))}
            />
            <FormField
              name='withholdingTax'
              label='Withholding Tax'
              type='number'
              error={touched.withholdingTax ? errors.withholdingTax : undefined}
            />
            <FormField
              name='contractSize'
              label='Contract Size'
              type='number'
              error={touched.contractSize ? errors.contractSize : undefined}
            />
            <FormField
              name='digits'
              label='Digits'
              type='number'
              error={touched.digits ? errors.digits : undefined}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={handleSubmitForm} disabled={isSubmitting || !isValid} type='button'>
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

const dataToSubmit = (
  values: AddEditInstrumentFormValues,
  props: OuterProps
): FormSubmitValues<AddUpdateInstrument<string>> => {
  const currency = props.currencies.find((x) => x.id === values.baseCurrencyId)

  if (currency) {
    return { ...values, baseCurrencyId: currency.id }
  }

  return values
}

export const AddEditInstrumentForm = withFormik<OuterProps, AddEditInstrumentFormValues>({
  mapPropsToValues: ({ instrument }) => {
    const currentInstrument: Partial<InstrumentDto<string>> = instrument || {}

    return {
      id: currentInstrument.id,
      typeId: currentInstrument?.type?.id || 1,
      name: currentInstrument?.name || '',
      symbol: currentInstrument?.symbol || '',
      baseCurrencyId: currentInstrument?.baseCurrency?.id || '',
      quoteCurrencyId: currentInstrument?.quoteCurrency?.id || '',
      withholdingTax: currentInstrument?.withholdingTax || 0,
      contractSize: currentInstrument?.contractSize || 0,
      digits: currentInstrument?.digits || 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditInstrumentFormValues> = {}
    if (!values.symbol) {
      errors.symbol = 'Required'
    }

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.baseCurrencyId) {
      errors.baseCurrencyId = 'Required'
    }

    if (!values.quoteCurrencyId) {
      errors.quoteCurrencyId = 'Required'
    }

    if (values.withholdingTax < 0) {
      errors.withholdingTax = 'Must be greater or equal to 0.'
    }

    if (!isNumeric(values.withholdingTax)) {
      errors.withholdingTax = 'Invalid Withholding Tax.'
    }

    if (values.contractSize < 0) {
      errors.contractSize = 'Must be greater or equal to 0.'
    }

    if (!Number.isInteger(Number(values.contractSize))) {
      errors.contractSize = 'Contract size must be an integer.'
    }

    if (!isNumeric(values.contractSize)) {
      errors.contractSize = 'Invalid Contract size.'
    }

    if (values.digits < 0) {
      errors.digits = 'Must be greater or equal to 0.'
    }

    if (!Number.isInteger(Number(values.digits))) {
      errors.digits = 'Digits must be an integer.'
    }

    if (!isNumeric(values.digits)) {
      errors.digits = 'Invalid Digits.'
    }

    return errors
  },
  enableReinitialize: true,
})(AddEditInstrumentFormUI)
